import { Bell, Inbox } from "lucide-react";
import { useEffect } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { resolveNotificationSource } from "@/modules/dashboard/notifications/config/notification-sources.config";
import { useNotifications } from "@/modules/dashboard/notifications/hooks/useNotifications";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaPopover } from "@/shared/components/overlays/ColocaPopover";
import { cn } from "@/shared/lib/utils";

export type AppShellNotificationsBellLabels = {
  title: string;
  emptyTitle: string;
};

export type ColocaAppShellNotificationsBellProps = {
  labels: AppShellNotificationsBellLabels;
};

export function ColocaAppShellNotificationsBell({ labels }: ColocaAppShellNotificationsBellProps) {
  const { user } = useAuth();
  const source = user ? resolveNotificationSource(user.user_type) : null;
  const { items, unreadCount, loadList, markAsRead } = useNotifications();

  useEffect(() => {
    if (!source?.enabled) return;
    loadList();
  }, [loadList, source?.enabled]);

  if (!source?.enabled) return null;

  return (
    <div className="relative inline-flex">
      <ColocaPopover
        trigger={<ColocaIconButton icon={Bell} className="rounded-full" />}
        title={labels.title}
        contentClassName="min-w-md max-w-md"
        align="end"
        sideOffset={12}
      >
        {items.length === 0 ? (
          <ColocaEmpty title={labels.emptyTitle} icon={Inbox}>
            <div></div>
          </ColocaEmpty>
        ) : (
          <ul className="flex max-h-96 flex-col gap-1 overflow-y-auto">
            {items.map((notification) => {
              const isUnread = !notification.read_at;
              return (
                <li key={notification.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (isUnread) markAsRead(notification.id);
                    }}
                    className={cn(
                      "flex w-full flex-col items-start gap-1 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted",
                      isUnread && "bg-muted/50",
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="truncate font-medium">{notification.title}</span>
                      {isUnread ? (
                        <span className="size-2 shrink-0 rounded-full bg-primary" aria-hidden />
                      ) : null}
                    </div>
                    {notification.summary ? (
                      <span className="text-xs text-muted-foreground line-clamp-2">
                        {notification.summary}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </ColocaPopover>
      {unreadCount > 0 ? (
        <ColocaBadge
          shape="circle"
          className="pointer-events-none absolute -top-1 -right-1 size-4 text-[10px] leading-none"
        >
          {unreadCount > 99 ? "99+" : unreadCount}
        </ColocaBadge>
      ) : null}
    </div>
  );
}
